import {
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  formatConfirmedDateISO,
  formatConfirmedDayOfWeek,
  formatConfirmedTime,
  formatFullDate,
  formatSlotTime,
} from './moveInAppointmentFormatters';
import { downloadMoveInICS } from './moveInAppointmentIcs';

export function MoveInConfirmedCard({
  startTime,
  endTime,
  onReschedule,
  lease,
  rescheduleAvailable = true,
}: {
  startTime: string;
  endTime: string;
  onReschedule: () => void;
  lease?: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number; address?: string | null } | null;
  rescheduleAvailable?: boolean;
}) {
  return (
    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-green-200 dark:border-green-900/40">
        <CheckCircle2 size={18} className="text-green-600 shrink-0" />
        <h3 className="text-sm font-semibold text-green-900">Your move-in is scheduled</h3>
      </div>
      <div className="divide-y divide-green-100">
        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Date</span>
          <span className="text-sm text-green-900">{formatConfirmedDateISO(startTime)}</span>
        </div>
        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Day</span>
          <span className="text-sm text-green-900">{formatConfirmedDayOfWeek(startTime)}</span>
        </div>
        <div className="grid grid-cols-[100px_1fr] px-5 py-2.5">
          <span className="text-xs font-medium text-green-700 dark:text-green-400 uppercase tracking-wide">Time</span>
          <span className="text-sm text-green-900">{formatConfirmedTime(startTime, endTime)}</span>
        </div>
      </div>
      <div className={`flex items-center px-5 py-3 border-t border-green-200 ${rescheduleAvailable ? 'justify-between' : 'justify-start'}`}>
        <button
          onClick={() => downloadMoveInICS(startTime, endTime, lease)}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors"
        >
          <CalendarPlus size={14} />
          Add to Calendar
        </button>
        {rescheduleAvailable ? (
          <button
            onClick={onReschedule}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-green-300 bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 hover:bg-green-100 transition-colors"
          >
            <RefreshCw size={14} />
            Reschedule
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function MoveInPendingConfirmCard({
  scheduledDate,
  onConfirm,
  onBookDifferent,
  confirming,
}: {
  scheduledDate: string;
  onConfirm: () => void;
  onBookDifferent: () => void;
  confirming: boolean;
}) {
  const fullDate = formatFullDate(scheduledDate);
  const timeStr = formatSlotTime(scheduledDate);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5">
        <CalendarDays size={18} className="text-red-700 dark:text-red-400 shrink-0" />
        <h3 className="text-sm font-semibold text-red-900 dark:text-red-200">Your move-in appointment is scheduled for:</h3>
      </div>
      <p className="text-lg font-semibold text-red-900 dark:text-red-200">
        {fullDate} at {timeStr} ET
      </p>
      <p className="text-sm text-red-700 dark:text-red-400">
        Please confirm this appointment or choose to book a different time.
      </p>
      <div className="flex flex-col items-center gap-3 pt-4 border-t border-red-200 dark:border-red-900/40">
        <button
          onClick={onConfirm}
          disabled={confirming}
          className="inline-flex items-center justify-center gap-2 w-full text-sm font-semibold px-6 py-3 rounded-xl bg-red-700 text-white hover:bg-red-800 transition-colors disabled:opacity-50 shadow-sm"
        >
          {confirming ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
          Confirm This Date
        </button>
        <button
          onClick={onBookDifferent}
          disabled={confirming}
          className="inline-flex items-center justify-center gap-2 w-full text-sm font-medium px-6 py-2.5 rounded-xl border border-red-300 bg-white dark:bg-gray-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
        >
          <CalendarPlus size={16} />
          Book a Different Time
        </button>
      </div>
    </div>
  );
}
